/**
 * `escLayers` — COMBAT.app's one Esc route (`decisions/0189`). Surfaces register an entry in a
 * **fixed layer**; one root `Escape` listener (`useCombatKeyboard`) dismisses the entry in the
 * highest layer that has one, does exactly that one thing, and stops. Recency orders entries only
 * *within* a layer (windows by focus); it never orders entries across layers.
 *
 * The registrable layers, top first, with their 0189 numbers:
 *
 * | # | Layer          | Members                                                         |
 * |---|----------------|-----------------------------------------------------------------|
 * | 1 | `popover`      | glossary `Term`, room switcher, brand menu, round header to-act |
 * | 2 | `gesture`      | an in-progress drag, wall run, room box, polyline, ping hold     |
 * | 3 | `armed`        | the aim, a terrain brush or tool, a Draw tool (one on the board) |
 * | 4 | `menuScreen`   | a non-root command-menu screen                                  |
 * | 5 | `window`       | every `CombatWindow`, the prompt window while dismissible       |
 * | 6 | `annotation`   | the selected annotation stroke                                  |
 *
 * Layer 0 (text entry) and layer 7 (the selection floor) are not registry entries: the root
 * listener lets a focused field keep its own Esc, and clears the selection when `dismissTop`
 * reports nothing was dismissed.
 *
 * Split like the rest of combat's infra: a **pure model** (the `EscEntry[]` transitions, tested in
 * `escLayers.test.ts`) plus the thin {@link useEscLayers} hook that holds the entries in a ref, and
 * {@link useEscEntry}, which registers one surface while it is active. The entries are ref-held:
 * nothing renders off them, and the root listener reads the latest entries synchronously.
 */
import { createContext, useCallback, useEffect, useMemo, useRef } from "react";

/** The registrable Esc layers (`decisions/0189`), top first. */
export const ESC_LAYERS = ["popover", "gesture", "armed", "menuScreen", "window", "annotation"] as const;
export type EscLayer = (typeof ESC_LAYERS)[number];

/** A layer's 0189 number; a lower number is dismissed first. */
export const escLayerNumber = (layer: EscLayer): number => ESC_LAYERS.indexOf(layer) + 1;

/**
 * One registered dismissible: its layer, a stable id, and the action Esc runs to dismiss it.
 * `live` makes a **standing** entry for state held in refs (a drag in progress): Esc passes it by
 * while `live()` is false, and dismissing it leaves it registered for the next gesture.
 */
export type EscEntry = { layer: EscLayer; id: string; onDismiss: () => void; live?: () => boolean };

const isLive = (e: EscEntry): boolean => e.live?.() ?? true;

/**
 * Register an entry. A new id joins the top of its layer; an id already registered keeps its
 * place and takes the new layer and `onDismiss` (a re-register never changes the order).
 */
export function registerEntry(entries: EscEntry[], entry: EscEntry): EscEntry[] {
  const at = entries.findIndex((e) => e.id === entry.id);
  if (at < 0) return [...entries, entry];
  return entries.map((e, i) => (i === at ? entry : e));
}

/** Unregister an entry by id (no-op, same reference, if absent). */
export function unregisterEntry(entries: EscEntry[], id: string): EscEntry[] {
  const next = entries.filter((e) => e.id !== id);
  return next.length === entries.length ? entries : next;
}

/** Raise an entry to the top of its own layer (no-op, same reference, if absent or already there). */
export function raiseEntry(entries: EscEntry[], id: string): EscEntry[] {
  const e = entries.find((x) => x.id === id);
  if (!e || entries.at(-1)?.id === id) return entries;
  return [...entries.filter((x) => x.id !== id), e];
}

/**
 * The entry Esc dismisses next: the most recent live entry in the highest layer that has one, or
 * (with `only`) the most recent live entry in that one layer.
 */
export function topEntry(entries: EscEntry[], only?: EscLayer): EscEntry | undefined {
  let top: EscEntry | undefined;
  for (const e of entries) {
    if (!isLive(e) || (only !== undefined && e.layer !== only)) continue;
    if (!top || escLayerNumber(e.layer) <= escLayerNumber(top.layer)) top = e;
  }
  return top;
}

/**
 * One Esc press on the model: the entry it dismisses (or `null`) and the entries left after. A
 * dismissed entry leaves the registry unless it is a standing (`live`) entry. `only` confines the
 * press to one layer (the board's right-click disarms layer 3 whatever sits above it).
 */
export function pressEsc(entries: EscEntry[], only?: EscLayer): { dismissed: EscEntry | null; entries: EscEntry[] } {
  const top = topEntry(entries, only);
  if (!top) return { dismissed: null, entries };
  return { dismissed: top, entries: top.live ? entries : unregisterEntry(entries, top.id) };
}

/** The COMBAT.app Esc registry handle. */
export type EscLayers = {
  /** Register (or re-seat in place) a dismissible in `layer`; `onDismiss` runs when Esc reaches it. */
  register: (entry: EscEntry) => void;
  /** Unregister a dismissible by id (closed by other means, or unmounted). */
  unregister: (id: string) => void;
  /** Raise a dismissible to the top of its layer (e.g. its window was focused). */
  raise: (id: string) => void;
  /**
   * Dismiss the top entry and report its layer and id, or `null` when nothing is registered —
   * the caller then handles the floor (the selection).
   */
  dismissTop: () => { layer: EscLayer; id: string } | null;
  /** Dismiss the top entry of one layer only (the board's right-click disarms layer 3), or `null` when it has none. */
  dismissIn: (layer: EscLayer) => { layer: EscLayer; id: string } | null;
};

/** The Esc registry hook: the {@link EscEntry} list in a ref, behind a stable {@link EscLayers} handle. */
export function useEscLayers(): EscLayers {
  const entriesRef = useRef<EscEntry[]>([]);
  const register = useCallback((entry: EscEntry) => {
    entriesRef.current = registerEntry(entriesRef.current, entry);
  }, []);
  const unregister = useCallback((id: string) => {
    entriesRef.current = unregisterEntry(entriesRef.current, id);
  }, []);
  const raise = useCallback((id: string) => {
    entriesRef.current = raiseEntry(entriesRef.current, id);
  }, []);
  const dismissIn = useCallback((layer?: EscLayer) => {
    const { dismissed, entries } = pressEsc(entriesRef.current, layer);
    if (!dismissed) return null;
    entriesRef.current = entries;
    dismissed.onDismiss();
    return { layer: dismissed.layer, id: dismissed.id };
  }, []);
  const dismissTop = useCallback(() => dismissIn(), [dismissIn]);
  return useMemo(() => ({ register, unregister, raise, dismissTop, dismissIn }), [register, unregister, raise, dismissTop, dismissIn]);
}

/**
 * COMBAT.app's registry, for a shared component that also renders outside it (the glossary `Term`):
 * `null` everywhere else, where such a component keeps its own Esc handling.
 */
export const EscLayersContext = createContext<EscLayers | null>(null);

/**
 * Register one surface in `layer` while `active` (a `null` registry registers nothing). It registers when `active` turns true and
 * unregisters when it turns false or the component unmounts; new `onDismiss` and `live` closures
 * are read through refs, so a re-render never re-registers the entry or changes its place in the
 * layer. Pass `live` (with `active: true`) for state held in refs, which renders nothing to key on.
 */
export function useEscEntry(
  esc: EscLayers | null,
  entry: { layer: EscLayer; id: string; active: boolean; onDismiss: () => void; live?: () => boolean },
): void {
  const { layer, id, active, onDismiss, live } = entry;
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const liveRef = useRef(live);
  liveRef.current = live;
  const standing = live !== undefined;
  useEffect(() => {
    if (!active || !esc) return;
    esc.register({ layer, id, onDismiss: () => onDismissRef.current(), live: standing ? () => liveRef.current?.() ?? false : undefined });
    return () => esc.unregister(id);
  }, [esc, layer, id, active, standing]);
}
